import React from "react";
import ReactSwitch from "react-switch";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import classNames from "classnames";
import { RootState, AppDispatch } from "../../../../../redux/store";
import { MdEmail, MdVerifiedUser, MdPersonOff } from "react-icons/md";
import { updateConfig, setConfig } from "./posConfigSlice";
import { POSConfig } from "./constants";

const SWITCH_PROPS = {
    uncheckedIcon: false as const,
    checkedIcon: false as const,
    handleDiameter: 26,
    offColor: "#f7b4b8",
    offHandleColor: "#E30613",
    onColor: "#c2eddd",
    onHandleColor: "#34C38F",
    width: 50,
    height: 20,
};

export function PaperlessTicket() {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const config = useSelector((state: RootState) => state.posConfig.config);
    const ticket = config.PaperlessTicket;

    const updateTicket = (field: string, value: boolean) => {
        dispatch(updateConfig({ path: ["PaperlessTicket", field], value }));
    };

    const handleToggleActive = () => {
        const newActive = !ticket.isActive;
        if (!newActive) {
            dispatch(setConfig({
                ...config,
                PaperlessTicket: {
                    ...ticket,
                    isActive: false,
                    isEmailActive: false,
                    isConfirmationEmailActive: false,
                    isUnauthenticatedEmailActive: false,
                },
            } as POSConfig));
        } else {
            updateTicket("isActive", true);
        }
    };

    const handleToggleEmail = () => {
        const newValue = !ticket.isEmailActive;
        updateTicket("isEmailActive", newValue);
        if (!newValue) {
            updateTicket("isConfirmationEmailActive", false);
        }
    };

    const handleToggleConfirmationEmail = () => {
        updateTicket(
            "isConfirmationEmailActive",
            !ticket.isConfirmationEmailActive
        );
    };

    const handleToggleUnauthenticatedEmail = () => {
        updateTicket(
            "isUnauthenticatedEmailActive",
            !ticket.isUnauthenticatedEmailActive
        );
    };

    return (
        <div className="h-100 pos-tab-layout__clz">
            <div>
                <div className="pos-section-title__clz">
                    {t("Paperless ticket")}
                </div>
            </div>

            <div className="d-flex flex-column align-items-center pos-paperless-content__clz">
                {/* Toggle principal */}
                <div
                    className={classNames(
                        "d-flex align-items-center justify-content-between pos-paperless-section__clz pos-paperless-toggle-row__clz",
                        ticket.isActive
                            ? "pos-paperless-active__clz"
                            : "pos-paperless-inactive__clz"
                    )}
                >
                    <div className="d-flex align-items-center pos-paperless-toggle-icon-row__clz">
                        <MdEmail
                            size={28}
                            className={ticket.isActive
                                ? "pos-paperless-icon-active__clz"
                                : "pos-paperless-icon-inactive__clz"}
                        />
                        <span className="pos-paperless-title__clz">
                            {t("Activate paperless ticketing")}
                        </span>
                    </div>
                    <ReactSwitch
                        {...SWITCH_PROPS}
                        checked={ticket.isActive}
                        onChange={handleToggleActive}
                        data-testid="pos-paperless-isActive"
                        aria-label={t("Activate paperless ticketing")}
                    />
                </div>

                {/* Section : Client authentifié */}
                <div
                    className={classNames(
                        "pos-paperless-section__clz pos-paperless-subsection__clz",
                        { "pos-paperless-subsection-disabled__clz": !ticket.isActive }
                    )}
                >
                    <div className="d-flex align-items-center pos-paperless-subsection-header__clz">
                        <MdVerifiedUser
                            size={22}
                            className="pos-paperless-verified-icon__clz"
                        />
                        <span className="pos-paperless-subsection-title__clz">
                            {t("Authenticated customer with valid e-mail address")}
                        </span>
                    </div>
                    <div className="d-flex flex-column pos-paperless-subsection-body__clz">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="pos-paperless-option-label__clz">
                                {t("Activate the « Send receipt by e-mail » option:")}
                            </span>
                            <ReactSwitch
                                {...SWITCH_PROPS}
                                checked={ticket.isEmailActive}
                                onChange={handleToggleEmail}
                                disabled={!ticket.isActive}
                                data-testid="pos-paperless-isEmailActive"
                                aria-label={t(
                                    "Activate the « Send receipt by e-mail » option:"
                                )}
                            />
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="pos-paperless-option-label__clz">
                                {t(
                                    "Activate the «Check e-mail address » option before sending the ticket:"
                                )}
                            </span>
                            <ReactSwitch
                                {...SWITCH_PROPS}
                                checked={ticket.isConfirmationEmailActive}
                                onChange={handleToggleConfirmationEmail}
                                disabled={
                                    !ticket.isEmailActive || !ticket.isActive
                                }
                                data-testid="pos-paperless-isConfirmationEmailActive"
                                aria-label={t(
                                    "Activate the «Check e-mail address » option before sending the ticket:"
                                )}
                            />
                        </div>
                    </div>
                </div>

                {/* Section : Client non authentifié */}
                <div
                    className={classNames(
                        "pos-paperless-section__clz pos-paperless-subsection__clz",
                        { "pos-paperless-subsection-disabled__clz": !ticket.isActive }
                    )}
                >
                    <div className="d-flex align-items-center pos-paperless-subsection-header__clz">
                        <MdPersonOff
                            size={22}
                            className="pos-paperless-unauth-icon__clz"
                        />
                        <span className="pos-paperless-subsection-title__clz">
                            {t("Unauthenticated customer")}
                        </span>
                    </div>
                    <div className="pos-paperless-subsection-body__clz">
                        <div className="d-flex justify-content-between align-items-center">
                            <span className="pos-paperless-option-label__clz">
                                {t(
                                    'Activate the « Send receipt by email » option:'
                                )}
                            </span>
                            <ReactSwitch
                                {...SWITCH_PROPS}
                                checked={ticket.isUnauthenticatedEmailActive}
                                onChange={handleToggleUnauthenticatedEmail}
                                disabled={!ticket.isActive}
                                data-testid="pos-paperless-isUnauthenticatedEmailActive"
                                aria-label={`${t(
                                    "Unauthenticated customer"
                                )} — ${t(
                                    "Activate the « Send receipt by email » option:"
                                )}`}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
